import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SEO, getBreadcrumbSchema } from '@/components/seo/SEO';
import { 
  FloralCorner 
} from '@/components/ui/FloralElements';
import { 
  ShoppingBag,
  Gift,
  ExternalLink,
  ShoppingCart,
  Loader2,
  AlertCircle,
  Info,
  ArrowRight,
  X,
  MapPin
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export const ShopPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showNotice, setShowNotice] = useState(true);
  const [showCartPrompt, setShowCartPrompt] = useState(false);

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' }
  ];

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Listen for messages from iframe (for add-to-cart attempts)
  useEffect(() => {
    // Show cart prompt after user has been browsing for a while
    const timer = setTimeout(() => {
      setShowCartPrompt(true);
    }, 15000); // Show after 15 seconds of browsing

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Set a timeout to hide loading if iframe takes too long
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <SEO
        title="Shop Handmade Products"
        description="Shop handmade goat milk soaps, homemade jams, jellies, salsas, and handcrafted aprons from our Nebraska family ranch. All products made with love and natural ingredients."
        path="/shop"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
        keywords="buy goat milk soap, handmade soap Nebraska, homemade jams, jellies, salsas, handcrafted aprons, natural products, farm fresh"
      />
      {/* Sticky Checkout Notice Banner */}
      {showNotice && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground py-3 px-4 shadow-lg">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <div className="hidden sm:flex w-10 h-10 rounded-full bg-white/20 items-center justify-center flex-shrink-0">
                <Info className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base">
                  Browse products below! Ready to checkout?
                </p>
                <p className="text-xs sm:text-sm opacity-90">
                  Click the button to complete your purchase securely on Square
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://littlehouseonthetable.square.site/s/shop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-primary rounded-lg font-semibold hover:bg-white/90 transition-all text-sm whitespace-nowrap active:scale-95"
              >
                <ShoppingCart className="h-4 w-4" />
                Checkout on Square
                <ExternalLink className="h-4 w-4" />
              </a>
              <button
                onClick={() => setShowNotice(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Dismiss notice"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - Compact */}
      <section className="relative py-8 md:py-10 bg-gradient-hero overflow-hidden">
        <FloralCorner position="top-left" className="opacity-40" />
        <FloralCorner position="bottom-right" className="opacity-40" />
        
        <div className="container mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium mb-3"
            >
              <ShoppingBag className="h-4 w-4" />
              Shop Our Products
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3"
            >
              Handmade with Love
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-sm md:text-base text-muted-foreground mb-4"
            >
              Browse our products below. When ready, click "Checkout on Square" to complete your purchase!
            </motion.p>
            
            {/* Quick Action Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-3">
              <a
                href="https://littlehouseonthetable.square.site/s/shop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl active:scale-95"
              >
                <ShoppingCart className="h-5 w-5" />
                Open Full Store
                <ExternalLink className="h-4 w-4" />
              </a>
              <a
                href="https://app.squareup.com/gift/XQ29SAF76B1Q1/order"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-xl font-semibold hover:bg-accent/90 transition-all active:scale-95"
              >
                <Gift className="h-5 w-5" />
                Gift Cards
                <ExternalLink className="h-4 w-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Important Notice Card */}
      <section className="bg-amber-50 border-y border-amber-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-amber-800 text-sm">
                  How to Complete Your Order
                </p>
                <p className="text-amber-700 text-xs">
                  Browse products below → Click "Checkout on Square" → Add items to cart & pay securely
                </p>
              </div>
            </div>
            <a
              href="https://littlehouseonthetable.square.site/s/shop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
            >
              Go to Checkout
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Embedded Square Store */}
      <section className="relative bg-card">
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-card z-10">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading store...</p>
            </div>
          </div>
        )}

        {/* Square Store Iframe - Fully Interactive */}
        <div style={{ width: '100%', minHeight: '80vh' }} className="relative">
          <iframe
            src="https://littlehouseonthetable.square.site/s/shop"
            title="Little House on the Table - Square Shop"
            style={{ 
              width: '100%', 
              height: '80vh', 
              minHeight: '600px',
              border: 'none'
            }}
            loading="lazy"
            allow="payment"
            onLoad={handleIframeLoad}
          />
        </div>

        {/* Helpful Cart Prompt - Appears after browsing */}
        {showCartPrompt && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-20 sm:bottom-24 left-4 right-4 sm:left-auto sm:right-6 z-40 sm:max-w-sm"
          >
            <div className="bg-white rounded-xl shadow-2xl border border-primary/20 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground text-sm mb-1">
                      Ready to add to cart?
                    </p>
                    <p className="text-muted-foreground text-xs mb-3">
                      To add items and checkout, click below to shop on our Square store.
                    </p>
                    <div className="flex items-center gap-2">
                      <a
                        href="https://littlehouseonthetable.square.site/s/shop"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-all active:scale-95"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        Shop on Square
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      <button
                        onClick={() => setShowCartPrompt(false)}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                        aria-label="Dismiss"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Floating Checkout Button - Mobile */}
        <div className="fixed bottom-4 left-4 right-4 z-40 sm:hidden">
          <a
            href="https://littlehouseonthetable.square.site/s/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-primary text-primary-foreground rounded-xl font-semibold shadow-2xl hover:bg-primary/90 transition-all active:scale-95"
          >
            <ShoppingCart className="h-5 w-5" />
            Checkout on Square
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>

        {/* Floating Checkout Button - Desktop */}
        <div className="hidden sm:block fixed bottom-6 right-6 z-40">
          <a
            href="https://littlehouseonthetable.square.site/s/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-4 bg-primary text-primary-foreground rounded-xl font-semibold shadow-2xl hover:bg-primary/90 transition-all hover:scale-105 active:scale-95"
          >
            <ShoppingCart className="h-5 w-5" />
            Ready to Checkout?
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* After Shopping Section */}
      <section className="py-8 bg-primary/5 border-t border-border">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="font-serif text-xl font-bold text-foreground mb-3">
              Ready to Complete Your Order?
            </h3>
            <p className="text-muted-foreground mb-4 text-sm">
              Click the button below to open our Square store where you can add items to your cart and checkout securely. 
              After your purchase, you can return here to continue browsing!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://littlehouseonthetable.square.site/s/shop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl active:scale-95"
              >
                <ShoppingCart className="h-6 w-6" />
                Complete Checkout on Square
                <ExternalLink className="h-5 w-5" />
              </a>
              <a
                href="https://app.squareup.com/gift/XQ29SAF76B1Q1/order"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent/10 text-accent rounded-xl font-medium hover:bg-accent/20 transition-colors"
              >
                <Gift className="h-5 w-5" />
                Purchase Gift Card
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section className="py-6 bg-card border-t border-border">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
            {/* Store Pickup Info */}
            <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-lg">
              <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
              <div className="text-left">
                <p className="text-xs text-muted-foreground">Store Pickup</p>
                <p className="text-foreground font-medium text-sm">260 Table Road, Crawford, NE</p>
              </div>
            </div>
            
            {/* Contact Info */}
            <div>
              <p className="text-muted-foreground text-sm mb-1">
                Questions about our products?
              </p>
              <p className="text-foreground font-medium text-sm">
                Call: (308) 665-5400 | Email: wohlersbeef@gmail.com
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShopPage;
